import { FC, Fragment, useEffect, useState } from 'react';

// API
import apibridge from '../../../apibridge';

// REDUX
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ToastMessagesSlice } from '../../../store/slice/ToastMessages';

// PACKAGES
import { Dropdown, OverlayTrigger, Spinner } from 'react-bootstrap';
import { useImmer } from 'use-immer';

// UTILS
import { guid } from '../../../libs/utils';

// COMPONENTS
import TitleSection, { TitleSectionType } from '../../../components/TitleSection/TitleSection';
import SvgMask from '../../../components/_Helpers/SvgMask';
import StudentSearchDropdown from './RecordMultipleComponents/StudentSearchDropdown';
import PortalDropdownMenu from './RecordMultipleComponents/PortalDropdownMenu';
import PortalTooltip from './RecordMultipleComponents/PortalTooltip';
import DelayedFadeIn from '../../../components/_Helpers/DelayedFadeIn';

// TYPES
import {
	AssessmentImportListAssessmentPeriod,
	AssessmentImportListClass,
	AssessmentImportListImportInfo,
	AssessmentStudentSearchStudent,
	AssessmentStudentStageMasteryStudent
} from '../../../api/models';

export type RecordView = 'Initial' | 'InProgress';

const AssessmentsRecordMultiple: FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const [isLoading, setIsLoading] = useState(false);
	const [isLoadingSelection, setIsLoadingSelection] = useState(false);

	// View state
	const [recordView, setRecordView] = useState<RecordView>('Initial');

	// Title Props set up
	const titleSectionProps: TitleSectionType = {
		title: 'Quick record multiple students',
		description: 'Quickly adjust the Stage mastery of multiple students for all record types in Reading and Spelling.',
		backToText: 'Back to Student Results',
		backToUrl: '/assessment-management?tabView=Results'
	};

	// States
	const [assessmentImportListInfo, setAssessmentImportListInfo] = useState<AssessmentImportListImportInfo | null>(null);

	// Selected Assessment Periods
	const [selectedAssessmentPeriod, setSelectedAssessmentPeriod] = useState<AssessmentImportListAssessmentPeriod | null>(
		null
	);

	// Selected Class
	const [selectedClass, setSelectedClass] = useState<AssessmentImportListClass | null>(null);

	// Student search states By Each Student or by Class
	const [searchedStudentsByClass, setSearchedStudentsByClass] = useState<AssessmentStudentSearchStudent[]>([]);

	// State to store the final details of the students when select either searchedStudents or searchedStudentsByClass
	const [searchedStudentsFinalsDetails, setSearchedStudentsFinalsDetails] = useState<AssessmentStudentSearchStudent[]>(
		[]
	);

	// Store the search term for the student search
	const [selectedStudent, setSelectedStudent] = useState<AssessmentStudentSearchStudent | null>(null);

	// Second search box states (for adding more students in progress view)
	const [selectedStudentAddExtra, setSelectedStudentAddExtra] = useState<AssessmentStudentSearchStudent | null>(null);

	// Get Assessment Import List Data
	const getAssessmentImportListData = async () => {
		setIsLoading(true);

		const response = await apibridge.getAssessmentImportList();

		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				setAssessmentImportListInfo(response.data.result);
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Assessment List error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}

		setIsLoading(false);
	};

	useEffect(() => {
		getAssessmentImportListData();
	}, []);

	const { assessmentPeriods, classes, gpkStages, readingStages, spellingStages } = assessmentImportListInfo || {};

	// MAIN STATE to store The students list to be uploaded
	const [studentsToUpload, setStudentsToUpload] = useImmer<AssessmentStudentStageMasteryStudent[]>([]);

	// State to track if user has modified stages for each student
	const [modifiedStages, setModifiedStages] = useState<
		Record<string, { gpk: boolean; reading: boolean; spelling: boolean }>
	>({});

	// State to track original stage values when data is first loaded
	const [originalStageValues, setOriginalStageValues] = useState<
		Record<string, { gpk?: string; reading?: string; spelling?: string }>
	>({});

	// Functions to GET all Data for selected Student / Class
	const getAssessmentStudentStageMasteryData = async (studentIds: string[]) => {
		if (!selectedAssessmentPeriod) return;

		setIsLoading(true);

		const response = await apibridge.postAssessmentStudentStageMastery({
			assessmentPeriodId: selectedAssessmentPeriod?.key,
			studentIds: studentIds
		});

		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				setStudentsToUpload(response.data.result.students || []);

				// Store original stage values for comparison
				const originalValues: Record<string, { gpk?: string; reading?: string; spelling?: string }> = {};

				response.data.result.students?.forEach((student) => {
					if (student.id) {
						originalValues[student.id] = {
							gpk: student.gpk,
							reading: student.reading,
							spelling: student.spelling
						};
					}
				});

				setOriginalStageValues(originalValues);

				setRecordView('InProgress');
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Assessment List error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}

		setIsLoading(false);
	};

	// Select Student By Class
	const confirmClassSelection = async () => {
		setIsLoadingSelection(true);

		setSearchedStudentsFinalsDetails(searchedStudentsByClass);

		const studentIds = searchedStudentsByClass
			.map((student) => student.id)
			.filter((id): id is string => id !== undefined);

		if (studentIds.length > 0) {
			await getAssessmentStudentStageMasteryData(studentIds);
		}

		setIsLoadingSelection(false);
	};

	// Select Student By Student
	const confirmStudentSelection = async () => {
		if (selectedStudent) {
			setIsLoadingSelection(true);

			setSearchedStudentsFinalsDetails([selectedStudent]);

			const studentIds = [selectedStudent.id].filter((id): id is string => id !== undefined);

			if (studentIds.length > 0) {
				await getAssessmentStudentStageMasteryData(studentIds);
			}

			setIsLoadingSelection(false);
		}
	};

	// Reset All States, aside from selectedAssessmentPeriod
	const clearAllStates = () => {
		setStudentsToUpload([]);
		setModifiedStages({});
		setOriginalStageValues({});
		setSelectedClass(null);
		setSearchedStudentsByClass([]);
		setSearchedStudentsFinalsDetails([]);
		setSelectedStudent(null);
		setSelectedStudentAddExtra(null);
		setRecordView('Initial');
	};

	// LOGIC TO CONFIRM UPLOADING THE DATA
	const confirmUploadData = async () => {
		setIsLoading(true);

		const response = await apibridge.postAssessmentImport({
			assessmentPeriodId: selectedAssessmentPeriod?.key,
			students: studentsToUpload.map((student) => ({
				id: student.id,
				gpkStage: student.gpk,
				readingStage: student.reading,
				spellingStage: student.spelling
			}))
		});

		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				navigate('/assessment-management?tabView=Results');

				clearAllStates();

				setSelectedAssessmentPeriod(null);

				dispatch(
					ToastMessagesSlice.actions.add({
						id: guid(),
						type: 'success',
						heading: 'Quick recording complete',
						description: `Stage mastery has been adjusted for ${studentsToUpload.length} students.`
					})
				);
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Assessment Submission error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}

		setIsLoading(false);
	};

	// Add more students to the current upload list
	const confirmAddExtraStudent = async () => {
		if (selectedStudentAddExtra) {
			setIsLoadingSelection(true);

			if (!selectedAssessmentPeriod) return;

			const response = await apibridge.postAssessmentStudentStageMastery({
				assessmentPeriodId: selectedAssessmentPeriod?.key,
				studentIds: [selectedStudentAddExtra.id || '']
			});

			if (response && response.data) {
				if (!response.data.isError && response.data.result) {
					const students = response.data.result.students;

					if (students && students.length > 0) {
						const newStudent = students[0];

						setStudentsToUpload((draft) => {
							draft.push(newStudent);
						});

						// Merge original stage values instead of overwriting
						setOriginalStageValues((prev) => {
							const newValues = { ...prev };

							students.forEach((student) => {
								if (student.id) {
									newValues[student.id] = {
										gpk: student.gpk,
										reading: student.reading,
										spelling: student.spelling
									};
								}
							});

							return newValues;
						});
					}
				} else if (response.data.validationErrors) {
					for (const err of response.data.validationErrors) {
						dispatch(
							ToastMessagesSlice.actions.add({
								id: guid(),
								type: 'danger',
								heading: 'Assessment List error',
								description: err.reason || 'Unknown error'
							})
						);
					}
				}
			}

			// Add the student details to searchedStudentsFinalsDetails
			setSearchedStudentsFinalsDetails((prev) => [...prev, selectedStudentAddExtra]);

			// Clear the second search states
			setSelectedStudentAddExtra(null);

			setIsLoadingSelection(false);
		}
	};

	// Handle assessment period selection
	const handleAssessmentPeriodSelect = (period: AssessmentImportListAssessmentPeriod) => {
		setSelectedAssessmentPeriod(period);
		// Reset class selection when period changes
		setSelectedClass(null);
		setSearchedStudentsByClass([]);
	};

	// Handle class selection
	const handleClassSelect = (classItem: AssessmentImportListClass) => {
		setSelectedClass(classItem);
		classSelectionHandler(classItem);
	};

	// Class Selection Handler
	const classSelectionHandler = async (classItem: AssessmentImportListClass) => {
		setIsLoadingSelection(true);

		const response = await apibridge.getAssessmentStudentSearch(classItem.id, '');

		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				setSearchedStudentsByClass(response.data.result.students || []);
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Assessment List error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}

		setIsLoadingSelection(false);
	};

	return (
		<div className="assessment-record-multiple-page">
			<TitleSection {...titleSectionProps}></TitleSection>

			<div className="container">
				{isLoading ? (
					<div className="d-flex align-items-center justify-content-center position-relative w-100">
						<DelayedFadeIn>
							<Spinner animation="border" role="status">
								<span className="visually-hidden">Loading...</span>
							</Spinner>
						</DelayedFadeIn>
					</div>
				) : (
					<>
						<div className="period-selection-wrapper">
							<p className="h4 mb-2">Quick record</p>

							<p className="mb-4 body-small fw-semibold text-shades-500">
								Choose the assessment period and which students you want to quickly record Stage mastery for.
							</p>

							<div className="form-group w-50">
								<label htmlFor="className" className="body-small mb-1">
									<strong>Assessment Period</strong>
								</label>

								<Dropdown>
									<Dropdown.Toggle className="custom-dropdown-toggle" disabled={recordView === 'InProgress'}>
										<span
											className={selectedAssessmentPeriod ? 'text-dark-text fw-normal' : 'text-shades-500 fw-normal'}
										>
											{selectedAssessmentPeriod ? selectedAssessmentPeriod.label : 'Please select...'}
										</span>
										<SvgMask path="/svg/chevron-down.svg" className="text-shades-500" width={16} height={16} />
									</Dropdown.Toggle>

									<Dropdown.Menu className="custom-dropdown-menu">
										{assessmentPeriods?.map((periodItem) => (
											<Dropdown.Item
												key={periodItem.key}
												onClick={() => handleAssessmentPeriodSelect(periodItem)}
												className="custom-dropdown-item"
											>
												{periodItem.label}
											</Dropdown.Item>
										))}
									</Dropdown.Menu>
								</Dropdown>
							</div>
						</div>

						{/* Initial View */}
						{recordView === 'Initial' && (
							<div className="multiple-selection-wrapper-initial">
								<p className="h3 mb-5">Get started by selecting a class or a student</p>

								{/* Class Selection */}
								<div className="d-flex justify-content-center mb-4 gap-3">
									<div className="form-group w-100">
										<Dropdown>
											<Dropdown.Toggle className="custom-dropdown-toggle" disabled={!selectedAssessmentPeriod}>
												<span className={selectedClass ? 'text-dark-text fw-normal' : 'text-shades-500 fw-normal'}>
													{selectedClass ? selectedClass.name : 'Select a class'}
												</span>
												<SvgMask path="/svg/chevron-down.svg" className="text-shades-500" width={16} height={16} />
											</Dropdown.Toggle>

											<Dropdown.Menu className="custom-dropdown-menu">
												{classes?.map((classItem) => (
													<Dropdown.Item
														key={classItem.id}
														onClick={() => handleClassSelect(classItem)}
														className="custom-dropdown-item"
													>
														{classItem.name}
													</Dropdown.Item>
												))}
											</Dropdown.Menu>
										</Dropdown>
									</div>

									<button
										className="btn btn-primary"
										onClick={confirmClassSelection}
										disabled={!selectedAssessmentPeriod || !selectedClass || isLoadingSelection}
									>
										Select class
									</button>
								</div>

								<span className="body-large fw-bold text-shades-500 mb-4">OR</span>

								{/* Student Selection */}
								<div className="d-flex justify-content-center mb-3 gap-3">
									<div className="form-group w-100">
										<StudentSearchDropdown
											selectedStudent={selectedStudent}
											onStudentSelect={setSelectedStudent}
											placeholder="Select a student"
											disabled={!selectedAssessmentPeriod}
										/>
									</div>

									<button
										className="btn btn-primary"
										onClick={confirmStudentSelection}
										disabled={!selectedAssessmentPeriod || !selectedStudent || isLoadingSelection}
									>
										Select student
									</button>
								</div>
							</div>
						)}

						{/* Progress View */}
						{recordView === 'InProgress' && (
							<div className="multiple-selection-wrapper-progress">
								<div className="info-box">
									<SvgMask path="/svg/info-solid.svg" className="text-vivid-blue" width={24} height={24} />

									<p className="mb-0 text-dark-text">
										Students with existing records denoted by the <strong>warning icon</strong> may have parts of their
										previous assessment scores overwritten if the Stage mastery selected is lower than the current
										mastery.
									</p>
								</div>

								<div className="d-flex justify-content-start align-items-end gap-3 mb-4">
									<div className="form-group">
										<label htmlFor="className" className="body-small mb-1">
											<strong>Add another student</strong>
										</label>

										<StudentSearchDropdown
											selectedStudent={selectedStudentAddExtra}
											onStudentSelect={setSelectedStudentAddExtra}
											placeholder="Search for a student..."
											disabled={!selectedAssessmentPeriod}
											excludeStudentIds={studentsToUpload.map((student) => student.id || '')}
										/>
									</div>

									<button
										className="btn btn-primary add-student-btn"
										onClick={confirmAddExtraStudent}
										disabled={!selectedStudentAddExtra || isLoadingSelection}
									>
										<SvgMask path="/svg/plus.svg" className="text-white" width={16} height={16} />
									</button>
								</div>

								<div
									className="record-row-wrapper"
									style={{ overflowY: 'hidden', overflowX: 'auto', maxWidth: '100%' }}
								>
									{/* Label Row */}
									<div className="record-row">
										<div className="d-flex align-items-center justify-content-start"></div>

										<div className="d-flex flex-column justify-content-center align-items-start">
											<p className="mb-0 body-small text-dark-text fw-bold">Student</p>
										</div>

										<div className="mb-0 body-small text-dark-text fw-bold">Grapheme-phoneme Knowledge</div>

										<div className="mb-0 body-small text-dark-text fw-bold">Reading (Non-words)</div>

										<div className="mb-0 body-small text-dark-text fw-bold">Spelling</div>
									</div>

									<div className="divider"></div>

									{/* Student Rows */}
									{studentsToUpload.map((studentItem, studentIndex) => {
										const currentStudentDetails = searchedStudentsFinalsDetails.find(
											(student) => student.id === studentItem.id
										);

										const currentGPKStage = gpkStages?.find((stage) => stage.id === studentItem.gpk);
										const currentReadingStage = readingStages?.find((stage) => stage.id === studentItem.reading);
										const currentSpellingStage = spellingStages?.find((stage) => stage.id === studentItem.spelling);

										// Check if user has modified stages and if original values exist
										const studentId = studentItem.id;
										const hasOriginalGPK = studentId && originalStageValues[studentId]?.gpk;
										const hasOriginalReading = studentId && originalStageValues[studentId]?.reading;
										const hasOriginalSpelling = studentId && originalStageValues[studentId]?.spelling;
										const hasModifiedGPK = studentId && modifiedStages[studentId]?.gpk;
										const hasModifiedReading = studentId && modifiedStages[studentId]?.reading;
										const hasModifiedSpelling = studentId && modifiedStages[studentId]?.spelling;

										return (
											<Fragment key={`student-${studentItem.id || studentIndex}`}>
												<div className="record-row">
													<div className="d-flex align-items-center justify-content-start text-shades-500">
														{studentIndex + 1}
													</div>

													<div className="d-flex flex-column justify-content-center align-items-start">
														<p className="mb-0 body-medium text-dark-text fw-semibold">
															{currentStudentDetails?.firstName} {currentStudentDetails?.lastName}
														</p>

														<p className="mb-0 body-small text-dark-text fw-semibold">
															{currentStudentDetails?.className}
														</p>
													</div>

													<div className="d-flex justify-content-start align-items-center gap-1">
														<Dropdown className="assessment-multiple-dropdown">
															<Dropdown.Toggle variant="dropdown">
																<div className="d-flex align-items-center justify-content-start gap-2">
																	<div className={`status-box stage-${currentGPKStage?.name}`}></div>
																	{currentGPKStage ? (
																		<span className="dropdown-display-text">Stage {currentGPKStage.name}</span>
																	) : (
																		<span className="dropdown-display-text">Select stage</span>
																	)}
																</div>
															</Dropdown.Toggle>

															<Dropdown.Menu as={PortalDropdownMenu}>
																{gpkStages?.map((stage) => (
																	<Dropdown.Item
																		key={stage.id}
																		onClick={() => {
																			if (studentItem.id) {
																				setStudentsToUpload((draft) => {
																					const targetStudent = draft.find((student) => student.id === studentItem.id);

																					if (targetStudent) {
																						targetStudent.gpk = stage.id as string;
																					}
																				});

																				// Track that user has modified GPK stage
																				const studentId = studentItem.id;
																				setModifiedStages((prev) => ({
																					...prev,
																					[studentId]: {
																						...prev[studentId],
																						gpk: true
																					}
																				}));
																			}
																		}}
																		as="button"
																	>
																		Stage {stage?.name}
																	</Dropdown.Item>
																))}
															</Dropdown.Menu>
														</Dropdown>

														<div className="alert-icon-wrapper">
															{hasOriginalGPK && hasModifiedGPK && (
																<OverlayTrigger
																	placement="top"
																	delay={{ show: 100, hide: 0 }}
																	overlay={
																		<PortalTooltip id="b-13">
																			An existing record may be affected by this change
																		</PortalTooltip>
																	}
																>
																	<span className="d-inline-block">
																		<SvgMask
																			path="/svg/alert-solid.svg"
																			width={24}
																			height={24}
																			className="text-hero-rose"
																		/>
																	</span>
																</OverlayTrigger>
															)}
														</div>
													</div>

													<div className="d-flex justify-content-start align-items-center gap-1">
														<Dropdown className="assessment-multiple-dropdown">
															<Dropdown.Toggle variant="dropdown">
																<div className="d-flex align-items-center justify-content-start gap-2">
																	<div className={`status-box stage-${currentReadingStage?.name}`}></div>
																	{currentReadingStage ? (
																		<span className="dropdown-display-text">Stage {currentReadingStage.name}</span>
																	) : (
																		<span className="dropdown-display-text">Select stage</span>
																	)}
																</div>
															</Dropdown.Toggle>

															<Dropdown.Menu as={PortalDropdownMenu}>
																{readingStages?.map((stage) => (
																	<Dropdown.Item
																		key={stage.id}
																		onClick={() => {
																			if (studentItem.id) {
																				setStudentsToUpload((draft) => {
																					const targetStudent = draft.find((student) => student.id === studentItem.id);

																					if (targetStudent) {
																						targetStudent.reading = stage.id as string;
																					}
																				});

																				// Track that user has modified Reading stage
																				const studentId = studentItem.id;
																				setModifiedStages((prev) => ({
																					...prev,
																					[studentId]: {
																						...prev[studentId],
																						reading: true
																					}
																				}));
																			}
																		}}
																		as="button"
																	>
																		Stage {stage?.name}
																	</Dropdown.Item>
																))}
															</Dropdown.Menu>
														</Dropdown>

														<div className="alert-icon-wrapper">
															{hasOriginalReading && hasModifiedReading && (
																<OverlayTrigger
																	placement="top"
																	delay={{ show: 100, hide: 0 }}
																	overlay={
																		<PortalTooltip id="b-12">
																			An existing record may be affected by this change
																		</PortalTooltip>
																	}
																>
																	<span className="d-inline-block">
																		<SvgMask
																			path="/svg/alert-solid.svg"
																			width={24}
																			height={24}
																			className="text-hero-rose"
																		/>
																	</span>
																</OverlayTrigger>
															)}
														</div>
													</div>

													<div className="d-flex justify-content-start align-items-center gap-1">
														<Dropdown className="assessment-multiple-dropdown">
															<Dropdown.Toggle variant="dropdown">
																<div className="d-flex align-items-center justify-content-start gap-2">
																	<div className={`status-box stage-${currentSpellingStage?.name}`}></div>
																	{currentSpellingStage ? (
																		<span className="dropdown-display-text">Stage {currentSpellingStage.name}</span>
																	) : (
																		<span className="dropdown-display-text">Select stage</span>
																	)}
																</div>
															</Dropdown.Toggle>

															<Dropdown.Menu as={PortalDropdownMenu}>
																{spellingStages?.map((stage) => (
																	<Dropdown.Item
																		key={stage.id}
																		onClick={() => {
																			if (studentItem.id) {
																				setStudentsToUpload((draft) => {
																					const targetStudent = draft.find((student) => student.id === studentItem.id);

																					if (targetStudent) {
																						targetStudent.spelling = stage.id as string;
																					}
																				});

																				// Track that user has modified Spelling stage
																				const studentId = studentItem.id;
																				setModifiedStages((prev) => ({
																					...prev,
																					[studentId]: {
																						...prev[studentId],
																						spelling: true
																					}
																				}));
																			}
																		}}
																		as="button"
																	>
																		Stage {stage?.name}
																	</Dropdown.Item>
																))}
															</Dropdown.Menu>
														</Dropdown>

														<div className="alert-icon-wrapper">
															{hasOriginalSpelling && hasModifiedSpelling && (
																<OverlayTrigger
																	placement="top"
																	delay={{ show: 100, hide: 0 }}
																	overlay={
																		<PortalTooltip id="b-16">
																			An existing record may be affected by this change
																		</PortalTooltip>
																	}
																>
																	<span className="d-inline-block">
																		<SvgMask
																			path="/svg/alert-solid.svg"
																			width={24}
																			height={24}
																			className="text-hero-rose"
																		/>
																	</span>
																</OverlayTrigger>
															)}
														</div>
													</div>

													<div className="d-flex justify-content-end align-items-center">
														<button
															className="btn btn-remove-record"
															onClick={() => {
																if (studentItem.id) {
																	setStudentsToUpload((draft) => {
																		const filteredStudents = draft.filter((student) => student.id !== studentItem.id);

																		// Check if no students remain after removal
																		if (filteredStudents.length === 0) {
																			// Reset view and clear all related states
																			clearAllStates();
																		}

																		return filteredStudents;
																	});
																}
															}}
														>
															<SvgMask path="/svg/cross-thin.svg" width={16} height={16} />
														</button>
													</div>
												</div>
												<div className="divider"></div>
											</Fragment>
										);
									})}
								</div>

								<div className="d-flex align-items-center justify-content-start gap-3 mt-5">
									<button
										className="btn btn-primary"
										onClick={confirmUploadData}
										disabled={studentsToUpload.length === 0}
									>
										Save and finish
									</button>

									<button className="btn btn-white" onClick={clearAllStates}>
										Start over
									</button>
								</div>
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
};

export default AssessmentsRecordMultiple;
